import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import CheckoutModal from '../components/CheckoutModal';
import DeepDiveSection from '../components/result/DeepDiveSection';
import DetectionViewer from '../components/result/DetectionViewer';
import PersonalInfoSection from '../components/result/PersonalInfoSection';
import ProductCards from '../components/result/ProductCards';
import RadarAnalysisSection from '../components/result/RadarAnalysisSection';
import ResultAnalysisSections from '../components/result/ResultAnalysisSections';
import SegmentationRenderer, { useSegmentation } from '../components/result/SegmentationRenderer';
import { usePageSetupLogo } from '../hooks/usePageSetupLogo';
import { useReportImages } from '../hooks/useReportImages';
import { useSalonProductCart } from '../hooks/useSalonProductCart';
import useSalonResultStore from '../store/salonResultStore';
import {
  fetchSalonReport,
  isAppSessionJwt,
  isSignedReportAccessToken,
  resolveSalonReferenceFromParams,
} from '../utils/salonReportApi';
import {
  REQUEST_STATUS,
  createRequestScope,
  isAbortError,
  isTimeoutError,
  logSalonResilience,
} from '../utils/salonResilience';

import '../styles/PageBackground.css';
import './SkinRoutinePage.css';
import './SalonResultPage.css';

function getReportAppSessionToken(report) {
  return String(report?.app_session_token || '').trim();
}

const SalonResultPageContent = ({ report, salonReference, appSessionToken }) => {
  const segmentation = useSegmentation();
  const orgLogoUrl = usePageSetupLogo(appSessionToken);
  const logoSrc = orgLogoUrl || `${process.env.PUBLIC_URL}/logo.svg`;
  const { images } = useReportImages(report, salonReference, appSessionToken);

  const {
    cartItems,
    checkoutCartItems,
    isCheckoutModalOpen,
    handleAddToCart,
    handleCheckout,
    handleCloseCheckoutModal,
    handleCartUpdate,
    handleViewProduct,
    getTotalCartItems,
    apiBaseUrl,
  } = useSalonProductCart({
    sessionId: salonReference,
    appSessionToken,
    pageName: 'SalonResultPage',
    initialCartAdded: report?.cart_added,
  });

  return (
    <>
      <div className="skin-routine-page-nav skin-result-page-nav" id="salon-result-page-nav">
        <div className="skin-routine-page-title-wrapper">
          {segmentation.addToCart && getTotalCartItems() > 0 && (
            <button
              type="button"
              className="skin-routine-page-checkout-button-icon"
              onClick={handleCheckout}
              title={`Checkout (${getTotalCartItems()} items)`}
              aria-label={`Checkout with ${getTotalCartItems()} items`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              <span className="skin-routine-page-checkout-badge-icon">{getTotalCartItems()}</span>
            </button>
          )}
          <img
            src={logoSrc}
            alt="Organization logo"
            className="skin-result-org-logo-nav"
          />
        </div>
      </div>

      <main className="skin-routine-page-main" id="salon-result-page-main">
        <div className="skin-routine-page-container" id="salon-result-page-container">
          <section className="skin-routine-page-layout" id="salon-result-page-layout">
            <div
              className="skin-routine-user-info-intro skin-text-body skin-text-medium"
              style={{ textAlign: 'center', margin: '0 0 1.2rem 0' }}
            >
              <span>
                This is your personalized skin analysis summary. Review your profile, score,
                and recommendations below for a complete overview of your skin health.
              </span>
            </div>

            <PersonalInfoSection report={report} userImage={images.front} />

            <div className="skin-routine-content-wrapper skin-routine-content-no-tabs skin-routine-sections">
              {!segmentation.loaded ? (
                <div className="skin-routine-summary-loading">
                  <div className="loading-spinner" />
                  <p className="skin-routine-summary-loading-text">Loading session settings…</p>
                </div>
              ) : (
                <>
                  <DetectionViewer
                    report={report}
                    skinReference={salonReference}
                    appSessionToken={appSessionToken}
                  />
                  <ResultAnalysisSections report={report} />
                  <DeepDiveSection report={report} />
                  <RadarAnalysisSection
                    report={report}
                    skinReference={salonReference}
                    appSessionToken={appSessionToken}
                  />
                  <ProductCards
                    products={report?.products}
                    cartItems={cartItems}
                    onAddToCart={handleAddToCart}
                    onViewProduct={handleViewProduct}
                  />

                  {segmentation.addToCart ? (
                    <div className="skin-routine-page-actions" id="salon-result-page-actions">
                      <div className="skin-routine-page-actions-buttons">
                        <button
                          type="button"
                          className="skin-action-button skin-routine-checkout-button"
                          onClick={handleCheckout}
                          title="Checkout"
                        >
                          Checkout
                        </button>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </section>
        </div>
      </main>

      <CheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        cartItems={checkoutCartItems}
        onCartUpdate={handleCartUpdate}
        sessionId={salonReference}
        apiBaseUrl={apiBaseUrl}
        token={appSessionToken}
      />
    </>
  );
};

const SalonResultLoadedView = ({ report, salonReference, appSessionToken }) => (
  <SegmentationRenderer
    key={appSessionToken || salonReference}
    appSessionToken={appSessionToken}
  >
    <SalonResultPageContent
      report={report}
      salonReference={salonReference}
      appSessionToken={appSessionToken}
    />
  </SegmentationRenderer>
);

const STATUS_COPY = {
  [REQUEST_STATUS.PROCESSING]: 'Loading your salon report…',
  [REQUEST_STATUS.RETRYING]: 'Retrying report load…',
  [REQUEST_STATUS.RECONNECTING]: 'Reconnecting to server…',
  [REQUEST_STATUS.TIMEOUT]: 'Report load timed out.',
};

const SalonResultPage = () => {
  const [params] = useSearchParams();
  const salonReference = resolveSalonReferenceFromParams(params);
  const queryToken = params.get('token') || '';
  const signedReportToken = useMemo(
    () => (isSignedReportAccessToken(queryToken) ? queryToken.trim() : ''),
    [queryToken],
  );

  useEffect(() => {
    const raw = params.get('token') || params.get('app_session_token') || '';
    if (!raw || isSignedReportAccessToken(raw)) return;
    if (!isAppSessionJwt(raw) && !params.get('app_session_token')) return;

    const next = new URLSearchParams(params);
    next.delete('token');
    next.delete('app_session_token');
    const qs = next.toString();
    const path = window.location.pathname;
    window.history.replaceState(null, '', qs ? `${path}?${qs}` : path);
    logSalonResilience('report_url_sanitized', {
      salonReference,
      removed: isAppSessionJwt(raw) ? 'app_session_jwt' : 'app_session_token',
    });
  }, [params, salonReference]);

  const {
    report,
    loading,
    error,
    requestStatus,
    setReport,
    setLoading,
    setError,
    setRequestStatus,
  } = useSalonResultStore();

  const appSessionToken = useMemo(
    () => getReportAppSessionToken(report),
    [report],
  );

  const requestScopeRef = useRef(null);
  const retryBusyRef = useRef(false);

  const abortActiveRequest = useCallback((reason) => {
    if (requestScopeRef.current) {
      logSalonResilience('abort', { label: 'salon-report-page', reason });
      requestScopeRef.current.abort(reason);
      requestScopeRef.current = null;
    }
  }, []);

  const loadReport = useCallback(async (ref, token, { isManualRetry = false } = {}) => {
    if (!ref || (isManualRetry && retryBusyRef.current)) return;

    if (isManualRetry) retryBusyRef.current = true;

    abortActiveRequest('retry-replacement');
    const scope = createRequestScope();
    requestScopeRef.current = scope;

    setLoading(true);
    setError(null);
    setRequestStatus(REQUEST_STATUS.PROCESSING);

    try {
      const data = await fetchSalonReport(ref, {
        accessToken: token || undefined,
        signal: scope.signal,
        onRetry: ({ reconnecting }) => {
          setRequestStatus(reconnecting ? REQUEST_STATUS.RECONNECTING : REQUEST_STATUS.RETRYING);
        },
      });

      if (scope.isAborted()) return;

      setReport(data);
      setRequestStatus(REQUEST_STATUS.IDLE);
    } catch (err) {
      if (isAbortError(err)) return;

      if (isTimeoutError(err)) {
        setError('Report load timed out. Please try again.');
        setRequestStatus(REQUEST_STATUS.TIMEOUT);
      } else if (err.status === 403 || err.apiResponse?.code === 'expired_token') {
        setError(err.message || 'This share link has expired.');
        setRequestStatus(REQUEST_STATUS.FAILED);
      } else if (err.status === 401 || err.apiResponse?.code === 'invalid_token') {
        setError(err.message || 'Invalid or missing access token.');
        setRequestStatus(REQUEST_STATUS.FAILED);
      } else {
        setError(err.message || 'Failed to load salon report');
        setRequestStatus(REQUEST_STATUS.RETRY_AVAILABLE);
      }

      setReport(null);
    } finally {
      retryBusyRef.current = false;
      if (!scope.isAborted()) {
        setLoading(false);
      }
      if (requestScopeRef.current === scope) {
        requestScopeRef.current = null;
      }
    }
  }, [abortActiveRequest, setError, setLoading, setReport, setRequestStatus]);

  useEffect(() => {
    if (!salonReference) {
      setError('Missing session_id in URL');
      setReport(null);
      setLoading(false);
      setRequestStatus(REQUEST_STATUS.FAILED);
      return undefined;
    }

    loadReport(salonReference, signedReportToken);
    return () => abortActiveRequest('unmount');
  }, [salonReference, signedReportToken, loadReport, abortActiveRequest, setError, setLoading, setReport, setRequestStatus]);

  const handleRetry = useCallback(() => {
    if (retryBusyRef.current) return;
    loadReport(salonReference, signedReportToken, { isManualRetry: true });
  }, [loadReport, salonReference, signedReportToken]);

  const statusText = STATUS_COPY[requestStatus] || STATUS_COPY[REQUEST_STATUS.PROCESSING];

  return (
    <div className="skin-routine-page-shell page-bg skin-only" id="salon-result-page-shell">
      {loading && (
        <main className="skin-routine-page-main">
          <div className="skin-routine-page-container">
            <div className="skin-routine-summary-loading" aria-live="polite">
              <div className="loading-spinner" />
              <p className="skin-routine-summary-loading-text">{statusText}</p>
            </div>
          </div>
        </main>
      )}

      {error && !loading && (
        <main className="skin-routine-page-main">
          <div className="skin-routine-page-container">
            <div className="skin-routine-error skin-result-error">
              <p>{error}</p>
              {(requestStatus === REQUEST_STATUS.RETRY_AVAILABLE
                || requestStatus === REQUEST_STATUS.TIMEOUT) && (
                <button type="button" className="skin-action-button" onClick={handleRetry}>
                  Try Again
                </button>
              )}
            </div>
          </div>
        </main>
      )}

      {!loading && !error && report && (
        <SalonResultLoadedView
          report={report}
          salonReference={salonReference}
          appSessionToken={appSessionToken}
        />
      )}
    </div>
  );
};

export default SalonResultPage;
